
import React, { useMemo, useState } from "react";
import { ChatMessage as ChatMessageType } from "../types";
import { cn } from "../lib/utils";
import { HelpCircle, X } from "lucide-react";
import { stepHelpContent } from "../utils/stepHelpContent";

interface ChatMessageProps {
  message: ChatMessageType;
  showTime?: boolean;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message, showTime = true }) => {
  const isUser = message.isUser;
  const [showHelp, setShowHelp] = useState(false);
  const lines = useMemo(() => message.content.split('\n'), [message.content]);

  const helpContent = message.step ? stepHelpContent[message.step] : undefined;

  return (
    <div
      className={cn(
        "mb-4 animate-slide-in-up max-w-[85%] transition-all",
        isUser ? "ml-auto" : "mr-auto"
      )}
    >
      <div
        className={cn(
          "rounded-2xl px-4 py-3 text-foreground"
        )}
        style={{
          WebkitBackdropFilter: 'blur(24px)',
          backdropFilter: 'blur(24px)',
          background: isUser
            ? 'rgba(139, 92, 246, 0.40)'
            : 'rgba(0, 0, 0, 0.35)',
          border: isUser
            ? '1px solid rgba(139, 92, 246, 0.45)'
            : 'none',
        }}
      >
        <div className="whitespace-pre-line" style={{ textShadow: '0 1px 3px rgba(50,50,50,0.25)' }}>
          {lines.map((text, i) => (
            <React.Fragment key={i}>
              {text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/).map((part, j) =>
                part.startsWith('**') && part.endsWith('**') ? (
                  <strong key={j} className="font-semibold">{part.slice(2, -2)}</strong>
                ) : part.startsWith('*') && part.endsWith('*') && !part.startsWith('**') ? (
                  <em key={j} className="text-muted-foreground text-sm">{part.slice(1, -1)}</em>
                ) : (
                  <React.Fragment key={j}>{part}</React.Fragment>
                )
              )}
              {i < lines.length - 1 && <br />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {(showTime || (!isUser && helpContent)) && (
        <div className="flex items-center gap-2 mt-1 justify-end">
          {!isUser && helpContent && (
            <button
              onClick={() => setShowHelp(!showHelp)}
              className="text-xs text-muted-foreground hover:text-accent flex items-center gap-1 transition-colors rounded-full px-2 py-0.5"
              style={{
                WebkitBackdropFilter: 'blur(24px)',
                backdropFilter: 'blur(24px)',
                background: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <HelpCircle size={13} />
              <span>Help me answer</span>
            </button>
          )}
          {showTime && (
            <span className="text-xs text-muted-foreground">
              {new Date(message.timestamp).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          )}
        </div>
      )}

      {showHelp && helpContent && (
        <div
          className="mt-2 rounded-xl px-4 py-3 text-sm animate-slide-in-up"
          style={{
            WebkitBackdropFilter: 'blur(24px)',
            backdropFilter: 'blur(24px)',
            background: 'rgba(139, 92, 246, 0.15)',
            border: '1px solid rgba(139, 92, 246, 0.3)',
          }}
        >
          <div className="flex items-start justify-between mb-2">
            <span className="font-semibold text-accent">{helpContent.title}</span>
            <button onClick={() => setShowHelp(false)} className="text-muted-foreground hover:text-foreground">
              <X size={14} />
            </button>
          </div>
          <p className="text-muted-foreground mb-2">
            <strong className="text-foreground">Why this step:</strong> {helpContent.purpose}
          </p>
          <p className="text-muted-foreground mb-2">
            <strong className="text-foreground">How to answer:</strong> {helpContent.howToAnswer}
          </p>
          <div>
            <strong className="text-foreground text-xs">Examples:</strong>
            <ul className="mt-1 space-y-1">
              {helpContent.examples.map((ex, i) => (
                <li key={i} className="text-muted-foreground text-xs pl-2 border-l-2 border-accent/30">
                  "{ex}"
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatMessage;
